import React, { useEffect, useState } from "react";
import axios from "axios";
import ListenButton from "./ListenButton";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_LABEL = { A: "Tap-Based", B: "Hands-Free" };

// Signed-in member's private progress view — past resets + recovery trend.
export default function MemberDashboard() {
  const { principal, isMember, loading, openLogin } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    // Wait for auth to resolve before deciding what to show — avoids a
    // zero-state flicker where empty tiles render before the session loads.
    if (loading) return undefined;
    if (!isMember) {
      setDataLoading(false);
      return undefined;
    }
    let active = true;
    setDataLoading(true);
    setLoadError(false);
    (async () => {
      try {
        const [s, h] = await Promise.all([
          axios.get(`${API}/me/sessions?limit=50`),
          axios.get(`${API}/recovery/history/${principal.id}?limit=24`),
        ]);
        if (!active) return;
        setSessions(s.data.sessions || []);
        setHistory(h.data.items || []);
      } catch {
        if (active) setLoadError(true);
      } finally {
        if (active) setDataLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isMember, principal, loading]);

  if (loading || dataLoading) return <Center text="Loading your progress…" />;

  if (!isMember) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="dashboard-signin-gate">
        <div className="iw-mono uppercase mb-3" style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}>
          My Progress
        </div>
        <h1 className="serif font-bold m-0 mb-4" style={{ color: "var(--iw-primary)", fontSize: "30px" }}>
          Sign in to see your progress
        </h1>
        <p className="text-base mb-6" style={{ color: "var(--iw-body)" }}>
          Your reset history and recovery-score trend are private to your member account.
        </p>
        <button className="iw-btn-primary" onClick={() => openLogin(null)} data-testid="dashboard-signin-btn">
          Sign in <span aria-hidden="true">↗</span>
        </button>
      </div>
    );
  }

  const completed = sessions.filter((x) => !x.partial);
  const latestScore = history.length ? history[history.length - 1].score : null;

  return (
    <main id="main" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="member-dashboard">
      <div className="iw-mono uppercase mb-2" style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}>
        My Progress
      </div>
      <h1 className="serif font-bold m-0" style={{ color: "var(--iw-primary)", fontSize: "clamp(28px,5vw,40px)" }}>
        Welcome back{principal.name ? `, ${principal.name.split(" ")[0]}` : ""}
      </h1>

      {loadError && (
        <div className="iw-card p-5 mt-6 flex items-center justify-between gap-4 flex-wrap" data-testid="dashboard-load-error">
          <p className="text-sm m-0" style={{ color: "var(--iw-accent-red)" }}>
            We couldn't load your progress just now.
          </p>
          <button className="iw-btn-ghost" style={{ padding: "0.5rem 0.9rem", fontSize: "12px" }}
            onClick={() => window.location.reload()} data-testid="dashboard-retry-btn">
            Try again
          </button>
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
        <Stat label="Resets completed" value={completed.length} testid="dash-stat-completed" />
        <Stat label="Total sessions" value={sessions.length} testid="dash-stat-total" />
        <Stat label="Recovery checks" value={history.length} testid="dash-stat-checks" />
        <Stat label="Latest score" value={latestScore != null ? `${latestScore}` : "—"} suffix={latestScore != null ? "/100" : ""} testid="dash-stat-latest" />
      </div>

      {/* Session pack / entitlement */}
      <EntitlementCard />

      {/* AI insights */}
      <InsightsCard />

      {/* Recovery trend */}
      <section className="iw-card p-6 sm:p-8 mt-8" data-testid="dashboard-recovery-trend">
        <h2 className="serif font-bold m-0 mb-1" style={{ color: "var(--iw-primary)", fontSize: "20px" }}>
          Recovery score trend
        </h2>
        <p className="text-sm m-0 mb-5" style={{ color: "var(--iw-muted)" }}>
          Non-diagnostic wellness metric · higher is more recovered.
        </p>
        {history.length >= 2 ? (
          <TrendChart items={history} />
        ) : (
          <p className="text-sm m-0" style={{ color: "var(--iw-muted)" }}>
            Log a couple of recovery checks to see your trend here.
          </p>
        )}
      </section>

      {/* Session history */}
      <section className="iw-card p-6 sm:p-8 mt-8" data-testid="dashboard-session-list">
        <h2 className="serif font-bold m-0 mb-5" style={{ color: "var(--iw-primary)", fontSize: "20px" }}>
          Your resets
        </h2>
        {sessions.length === 0 ? (
          <p className="text-sm m-0" style={{ color: "var(--iw-muted)" }}>No resets yet — start your first one below.</p>
        ) : (
          <div className="flex flex-col divide-y" style={{ borderColor: "var(--iw-border-soft)" }}>
            {sessions.map((s) => (
              <SessionRow key={s.id} s={s} />
            ))}
          </div>
        )}
      </section>

      <div className="mt-8">
        <a href="/" className="iw-btn-primary" data-testid="dashboard-start-reset-btn">
          Start a reset <span aria-hidden="true">↗</span>
        </a>
      </div>
    </main>
  );
}

// Session pack status + inline code redemption.
function EntitlementCard() {
  const [state, setState] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/me/entitlement`);
      setState(data);
    } catch (e) {
      console.error("Entitlement load failed", e);
    }
  };
  useEffect(() => { load(); }, []);

  const redeem = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null); setNotice(null);
    try {
      const { data } = await axios.post(`${API}/me/redeem-code`, { code: code.trim() });
      setNotice(`Code redeemed — ${data.sessions_added} sessions added.`);
      setCode("");
      setState(data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "That code didn't work.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const unlock = state.next_available_at ? fmtDate(state.next_available_at) : null;

  return (
    <section className="iw-card p-6 sm:p-8 mt-8" data-testid="dashboard-entitlement">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h2 className="serif font-bold m-0 mb-1" style={{ color: "var(--iw-primary)", fontSize: "20px" }}>
            Your session pack
          </h2>
          <p className="text-sm m-0" style={{ color: "var(--iw-muted)" }} data-testid="entitlement-status-line">
            {state.sessions_remaining > 0
              ? `${state.sessions_remaining} of ${state.sessions_total_granted || 12} sessions remaining · one per week`
              : state.reason === "exhausted"
                ? "Program complete — contact the wellness team to continue."
                : "No active session pack — redeem your access code below."}
            {state.reason === "weekly_lock" && unlock ? ` · next session unlocks ${unlock}` : ""}
          </p>
        </div>
        <div className="font-mono flex-shrink-0" style={{ color: "var(--iw-primary)", fontSize: "34px", fontWeight: 700, fontFamily: "'Orbitron', sans-serif", lineHeight: 1 }} data-testid="entitlement-remaining">
          {state.sessions_remaining}<span style={{ fontSize: "14px", opacity: 0.5 }}> left</span>
        </div>
      </div>
      <form onSubmit={redeem} className="flex gap-3 mt-5 flex-wrap">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="IW-XXXX-XXXX"
          required
          className="px-4 py-2.5 bg-white outline-none iw-mono tracking-widest flex-1 min-w-[200px]"
          style={{ border: "1px solid var(--iw-border)", borderRadius: "2px", color: "var(--iw-primary)" }}
          data-testid="dashboard-redeem-input"
        />
        <button type="submit" className="iw-btn-primary" disabled={busy} data-testid="dashboard-redeem-btn">
          {busy ? "Checking…" : "Redeem code"} <span aria-hidden="true">↗</span>
        </button>
      </form>
      {err && <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="dashboard-redeem-error">{err}</p>}
      {notice && <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-primary)" }} data-testid="dashboard-redeem-notice">{notice}</p>}

      <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--iw-border-soft)" }}>
        <p className="iw-mono uppercase m-0 mb-3" style={{ color: "var(--iw-muted)", fontSize: "10px", letterSpacing: "0.18em", fontWeight: 600 }}>
          Or buy sessions online
        </p>
        <div className="flex gap-3 flex-wrap">
          <BuyButton lookupKey="intro_single" label="Single Intro Session · $39" testId="buy-intro-btn" />
          <BuyButton lookupKey="reset_12" label="12-Session Program · $349" testId="buy-12pack-btn" />
        </div>
        <p className="text-xs mt-2 m-0" style={{ color: "var(--iw-muted)" }}>
          Secure checkout by Stripe. Sessions are added to your account instantly after payment.
        </p>
      </div>
    </section>
  );
}

function BuyButton({ lookupKey, label, testId }) {
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      console.error("Checkout failed", e);
      setBusy(false);
    }
  };
  return (
    <button type="button" className="iw-btn-ghost" style={{ padding: "0.6rem 1rem", fontSize: "12px" }}
      onClick={buy} disabled={busy} data-testid={testId}>
      {busy ? "Opening checkout…" : label} <span aria-hidden="true">↗</span>
    </button>
  );
}

// GPT-5.6 Sol personalized summary of the member's recent activity.
function InsightsCard() {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState(null);
  const [err, setErr] = useState(null);

  const generate = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await axios.post(`${API}/me/insights`);
      setText(data.insights);
    } catch (e) {
      console.error("Insights generation failed", e);
      setErr("Insights are unavailable right now — please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="iw-card p-6 sm:p-8 mt-8" data-testid="dashboard-insights">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="serif font-bold m-0 mb-1" style={{ color: "var(--iw-primary)", fontSize: "20px" }}>
            AI insights
          </h2>
          <p className="text-sm m-0" style={{ color: "var(--iw-muted)" }}>
            A personal read on your recent resets and recovery trend. Wellness guidance, not medical advice.
          </p>
        </div>
        <button
          className="iw-btn-primary flex-shrink-0"
          onClick={generate}
          disabled={busy}
          data-testid="dashboard-insights-btn"
        >
          {busy ? "Reading your trend…" : text ? "Refresh insights" : "Generate my insights"}{" "}
          <span aria-hidden="true">↗</span>
        </button>
      </div>
      {err && (
        <p className="text-sm mt-4 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="dashboard-insights-error">
          {err}
        </p>
      )}
      {text && (
        <div
          className="mt-5 p-5 text-base leading-relaxed"
          style={{ background: "var(--iw-bg)", border: "1px solid var(--iw-border-soft)", borderRadius: "2px", color: "var(--iw-body)" }}
          data-testid="dashboard-insights-text"
        >
          {text}
          <div className="mt-3">
            <ListenButton text={text} size={30} label="Listen" testId="dashboard-insights-listen-btn" />
          </div>
        </div>
      )}
    </section>
  );
}

// Pull human-readable reflection text out of the stored notes blob, dropping
// internal tags/markers. Returns "" when there's nothing worth showing.
function extractReflection(notes) {
  if (!notes) return "";
  let text = notes
    .replace(/\[MEMBER[^\]]*\]/gi, "")
    .replace(/\[PARTIAL[^\]]*\]/gi, "")
    .replace(/\[REFLECTION\]/gi, "")
    .replace(/State:\s*[^·]*/gi, "")
    .replace(/\s*·\s*·\s*/g, " · ")
    .replace(/^\s*·\s*|\s*·\s*$/g, "")
    .trim();
  return text.length >= 3 ? text : "";
}

function SessionRow({ s }) {
  const [open, setOpen] = React.useState(false);
  const reflection = extractReflection(s.notes);
  return (
    <div className="py-3" data-testid={`dashboard-session-${s.id}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-semibold" style={{ color: "var(--iw-primary)" }}>
            {MODE_LABEL[s.mode] || s.mode} reset{s.partial ? " · partial" : ""}
          </div>
          <div className="iw-mono text-xs" style={{ color: "var(--iw-muted)" }}>
            {fmtDate(s.timestamp || s.started_at)} · activation {s.pre_suds}
            {s.post_suds != null ? ` → ${s.post_suds}` : ""}
          </div>
          {reflection && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="iw-mono uppercase mt-1.5 inline-flex items-center gap-1.5"
              style={{ color: "var(--iw-blue)", fontSize: "10px", letterSpacing: "0.14em", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}
              data-testid={`dashboard-reflection-toggle-${s.id}`}
            >
              <span aria-hidden="true">{open ? "−" : "+"}</span> {open ? "Hide reflection" : "Read reflection"}
            </button>
          )}
        </div>
        <div className="text-right">
          {s.recovery_score != null ? (
            <span className="font-mono" style={{ color: "var(--iw-primary)", fontSize: "20px", fontWeight: 700, fontFamily: "'Orbitron', sans-serif" }}>
              {s.recovery_score}<span style={{ fontSize: "12px", opacity: 0.5 }}>/100</span>
            </span>
          ) : (
            <span className="iw-mono text-xs" style={{ color: "var(--iw-muted)" }}>—</span>
          )}
        </div>
      </div>
      {open && reflection && (
        <div
          className="mt-3 p-4 text-sm leading-relaxed"
          style={{ background: "var(--iw-bg)", border: "1px solid var(--iw-border-soft)", borderRadius: "2px", color: "var(--iw-body)" }}
          data-testid={`dashboard-reflection-body-${s.id}`}
        >
          {reflection}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, suffix = "", testid }) {  return (
    <div className="iw-card p-5" data-testid={testid}>
      <div className="font-mono" style={{ color: "var(--iw-primary)", fontSize: "34px", fontWeight: 700, fontFamily: "'Orbitron', sans-serif", lineHeight: 1 }}>
        {value}<span style={{ fontSize: "14px", opacity: 0.5 }}>{suffix}</span>
      </div>
      <div className="iw-mono uppercase mt-2" style={{ color: "var(--iw-muted)", fontSize: "9px", letterSpacing: "0.16em", fontWeight: 600 }}>
        {label}
      </div>
    </div>
  );
}

function TrendChart({ items }) {
  const scores = items.map((i) => i.score);
  const w = 640;
  const h = 120;
  // Scale to the data range (with padding) so real variation is visible.
  const pad = 5;
  const min = Math.max(0, Math.min(...scores) - pad);
  const max = Math.min(100, Math.max(...scores) + pad);
  const pts = scores.map((s, i) => {
    const x = scores.length === 1 ? w / 2 : (i / (scores.length - 1)) * w;
    const y = h - ((s - min) / (max - min || 1)) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const last = pts[pts.length - 1].split(",");
  const first = items[0];
  const latest = items[items.length - 1];
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="iw-mono uppercase" style={{ color: "var(--iw-muted)", fontSize: "9px", letterSpacing: "0.16em" }}>
          {fmtDate(first.created_at)}
        </span>
        <span className="iw-mono uppercase" style={{ color: "var(--iw-primary)", fontSize: "9px", letterSpacing: "0.16em", fontWeight: 700 }}>
          latest {latest.score}/100 · {fmtDate(latest.created_at)}
        </span>
      </div>
      <svg width="100%" height={h + 10} viewBox={`0 0 ${w} ${h + 10}`} preserveAspectRatio="none" aria-hidden="true">
        <polyline points={pts.join(" ")} fill="none" stroke="var(--iw-primary)" strokeWidth="2.5" transform="translate(0,5)" />
        <circle cx={last[0]} cy={Number(last[1]) + 5} r="4.5" fill="var(--iw-accent-red)" />
      </svg>
      <div className="iw-mono uppercase mt-2" style={{ color: "var(--iw-muted)", fontSize: "9px", letterSpacing: "0.16em" }}>
        Last {scores.length} checks · range {Math.min(...scores)}–{Math.max(...scores)}
      </div>
    </div>
  );
}

function Center({ text }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ color: "var(--iw-muted)" }}>
      {text}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
